import { Injectable } from '@angular/core';
import {
  ClientEvents,
  IClientSocket,
  IInstallation,
  WsNamespaces,
} from 'common_library';
import { SocketIoService, WS_REQUEST_TIMEOUT_MS } from '../communication/socket-io.service';
import { InstallationProperties } from 'src/app/components/map/map.component';
import { SelectedClusterService } from './selected-cluster.service';
import { MainStateService } from '../state/app-main-state.service';
import { HttpIoService } from '../communication/http-io.service';
import { LoadingService } from '../utils/loading.service';
import { ToastService } from '../utils/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class SelectedInstallationService {

  constructor(
    private SIS: SocketIoService,
    private ScS: SelectedClusterService,
    private mainStateService: MainStateService,
    private HIO: HttpIoService,
    private loading: LoadingService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {

    this.SIS.fromEvent<InstallationProperties[]>(
      WsNamespaces.Client,
      IClientSocket.ServerEvents.InstallationsUpdates
    ).subscribe(async (iu: InstallationProperties[]) => {
      console.log('SelectedInstallationService : installations_updates', iu);
      const selectedCluster = this.mainStateService.getSelectedCluster();
      if (selectedCluster) {
        this.ScS.updateCluster(iu);
      }
      const selectedInstallation = this.mainStateService.getSelectedInstallation();
      if (!selectedInstallation) return;
      if (iu.find((i) => i.id === selectedInstallation.id)) {
        console.log("***Update selected installation***", selectedInstallation.name)
        const rv = await this.fetchInstallation(selectedInstallation.id);
        this.mainStateService.setSelectedInstallation(rv);
      }
    });

    /**
     * "Qualcosa dentro l'installazione aperta è cambiato" (un connettore, una stazione).
     *
     * Serve un canale distinto da `installations_updates` perché l'aggregato dell'installazione è
     * una combinazione di N stazioni e può NON cambiare quando cambia un singolo connettore (basta
     * che un altro resti libero): senza questo, la scheda aperta resterebbe ferma su uno stato
     * vecchio. Il backend lo manda SOLO a chi ha questa installazione aperta (room dedicata),
     * quindi non genera traffico per gli altri client.
     *
     * ponytail: si rifà il fetch invece di patchare in place — è lo stesso path già usato qui sopra
     * per gli update d'installazione, quindi nessuna logica di merge nuova da sbagliare. Ceiling: un
     * round-trip per cambio, ma solo per chi ha la scheda aperta. Se diventasse pesante, la mossa è
     * arricchire il payload e patchare l'entità in memoria.
     */
    this.SIS.fromEvent<Array<{ id: string }>>(
      WsNamespaces.Client,
      IClientSocket.ServerEvents.InstallationDetailUpdates
    ).subscribe(async (du) => {
      const selectedInstallation = this.mainStateService.getSelectedInstallation();
      if (!selectedInstallation || !du?.length) return;
      if (!du.some((d) => d.id === selectedInstallation.id)) return;
      console.log('SelectedInstallationService : installation_detail_updates', du);
      const rv = await this.fetchInstallation(selectedInstallation.id);
      this.mainStateService.setSelectedInstallation(rv);
    });
  }

  /**
   * Fetch "silenzioso": usato anche dai refresh automatici via websocket, quindi non mostra nulla.
   * Restituisce `undefined` se fallisce — il feedback all'utente sta in `presentInstallationInfo`,
   * che è l'unico percorso avviato da un gesto dell'utente.
   */
  async fetchInstallation(id: string): Promise<IInstallation> {
    try {
      console.log(`fetching ${id} ...`);
      console.time(`fetched ${id}`);
      let rv: IInstallation;
      rv = await this.getInstallationFromSocket(id);
      console.timeEnd(`fetched ${id}`);
      return rv;
    } catch (error) {
      console.log('🐱️ : error', error);
    }
  }

  async getInstallationFromSocket(id: string): Promise<IInstallation> {
    let user = this.mainStateService.getUser();
    let installation = await this.SIS.sendRequest<{ installationId: string, userId?: string }, IInstallation>(
      WsNamespaces.Client,
      ClientEvents.GetInstallationById,
      {
        installationId: id,
        userId: user?.id
      }
    );
    // Deep link scaduto / QR di una colonnina dismessa: il backend risponde `data: null` e qui
    // partiva un TypeError su `.stations`. Errore esplicito → toast leggibile per l'utente.
    if (!installation) {
      throw new Error(`Installation not found: ${id}`);
    }
    const userVisibityGroups = this.mainStateService.getUserVisibilityGroups();
    const uvg = userVisibityGroups ? userVisibityGroups.groups : [];
    installation.stations = installation.stations.filter(s => s.visible || (s.visibilityGroups.filter(value => uvg.includes(value))).length > 0);
    return installation;
  }

  /**
   * Apertura richiesta dall'utente (pin sulla mappa, cluster, deep link, toast di sessione): unico
   * punto in cui si mostra l'attesa e si dichiara il fallimento. L'apertura costa secondi anche
   * quando va bene: senza indicatore l'utente crede che il tocco non sia stato registrato.
   */
  async presentInstallationInfo(id: string): Promise<IInstallation> {
    await this.loading.presentLoading({
      message: this.translate.instant('APP.INSTALLATION.LOADING'),
      duration: WS_REQUEST_TIMEOUT_MS + 2000,
      backdropDismiss: true,
    });
    try {
      const installation = await this.getInstallationFromSocket(id);
      this.mainStateService.setSelectedInstallation(installation);
      return installation;
    } catch (error) {
      console.log('🐱️ : error', error);
      await this.toast.presentBasicToast({
        message: this.translate.instant('TOAST_MESSAGES.INSTALLATION_NOT_LOADED'),
        color: 'danger',
        icon: 'warning',
      });
      return undefined;
    } finally {
      this.loading.dismissLoading();
    }
  }

  async getInstallationIdByStationId(stationId: string): Promise<{id: string | null}> {
    return await this.HIO.get<{id: string | null}>(`station/installation-id/${stationId.trim()}`);
  }
}
